import React from "react";
import { Link, useNavigate } from "react-router-dom";

import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  ClockIcon,
  GroupBlueIcon,
  AnnouncementPurpleIcon,
  CommandScheduleIcon,
  ContentScheduleIcon,
  AdsScheduleIcon,
} from "../../../icons";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
  TypographyXLarge,
} from "../../customComponent";
import { useSelector } from "react-redux";
import { colorStyling } from "../../../helpers/color";
import { purple } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import ViewerInfo from "../../viewer/id/ViewerInfo";

const RenderViewDetailViewer = ({ viewer, onClose }) => {
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.type;
  const { t: tCommon } = useTranslation("common");

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TypographyXLarge sx={{ fontWeight: 600 }}>
          {tCommon("viewDetail.viewerDetailDialogTitle")}
        </TypographyXLarge>

        <IconButton onClick={() => onClose(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Box
            sx={{
              border: `1px solid ${colorStyling.white.border}`,
              borderRadius: "6px",
              p: 2,
            }}
          >
            <ViewerInfo viewer={viewer} userRole={userRole} />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <GroupInfo viewer={viewer} />
        </Grid>

        <Grid item xs={12} lg={6}>
          <ScheduleInfo viewer={viewer} />
        </Grid>

        <Grid item xs={12}>
          <Link to={`/viewer/detail/${viewer?.id}`}>
            <Button sx={{ mt: 2 }} fullWidth variant="contained">
              {tCommon("viewDetail.moreDetails")}
            </Button>
          </Link>
        </Grid>
      </Grid>
    </>
  );
};

export default RenderViewDetailViewer;

const GroupInfo = ({ viewer }) => {
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  return (
    <Box>
      <TypographyXLarge sx={{ fontWeight: 500, mb: 1 }}>
        {tCommon("viewDetail.groupLabel")}
      </TypographyXLarge>

      <Box
        sx={{
          maxHeight: "300px",
          overflowY: "auto",
          border: `1px solid ${colorStyling.white.border}`,
          borderRadius: "6px",
          p: 2,
        }}
      >
        {Array.isArray(viewer?.screenGroups) && viewer?.screenGroups?.length ? (
          <Grid container spacing={2}>
            {viewer?.screenGroups.map((group) => (
              <Grid
                key={group.id}
                item
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box display="flex" alignItems="center">
                  <GroupBlueIcon />
                  <Tooltip
                    title={group?.name?.length > 30 ? group?.name : ""}
                    placement="top-start"
                  >
                    <TypographyLarge
                      sx={{
                        fontWeight: 600,
                        ml: 2,
                        cursor: "pointer",
                        "&:hover": {
                          color: colorStyling.primary,
                        },
                      }}
                      onClick={() => {
                        navigate("/group/detail/" + group.id);
                      }}
                    >
                      {group?.name?.length > 30
                        ? group?.name?.substring(0, 27).trim() + "..."
                        : group?.name}
                    </TypographyLarge>
                  </Tooltip>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Link to={"/group/detail/" + group.id}>
                    <IconButton sx={{ color: colorStyling.black, ml: 4 }}>
                      <Tooltip title={tCommon("viewDetail.seeDetails")}>
                        <NavigateNextIcon fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </Link>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TypographyLarge sx={{ textAlign: "center", fontWeight: 300 }}>
            {tCommon("viewDetail.noGroupData")}
          </TypographyLarge>
        )}
      </Box>
    </Box>
  );
};

const ScheduleInfo = ({ viewer }) => {
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  return (
    <Box>
      <TypographyXLarge sx={{ fontWeight: 500, mb: 1 }}>
        {tCommon("viewDetail.scheduleLabel")}
      </TypographyXLarge>

      <Box
        sx={{
          maxHeight: "300px",
          overflowY: "auto",
          border: `1px solid ${colorStyling.white.border}`,
          borderRadius: "6px",
          p: 2,
        }}
      >
        {Array.isArray(viewer?.schedules) && viewer?.schedules?.length ? (
          <Grid container spacing={2}>
            {viewer?.schedules?.map((schedule) => (
              <Grid
                key={schedule.id}
                item
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {schedule.type === "ANNOUNCEMENT" ? (
                    <AnnouncementPurpleIcon />
                  ) : schedule.type === "ADVERTISEMENT" ? (
                    <AdsScheduleIcon />
                  ) : schedule.type.includes("CONTENT") ? (
                    <ContentScheduleIcon />
                  ) : (
                    <CommandScheduleIcon />
                  )}

                  <Box sx={{ ml: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip
                        title={
                          schedule?.name?.length > 30 ? schedule?.name : ""
                        }
                        placement="top-start"
                      >
                        <TypographyNormal
                          sx={{
                            fontWeight: 600,
                            cursor: "pointer",
                            "&:hover": {
                              color: colorStyling.primary,
                            },
                          }}
                          onClick={() => {
                            navigate(
                              (schedule.type === "ANNOUNCEMENT"
                                ? "/announcement/detail/"
                                : "/schedule/detail/") + schedule.id
                            );
                          }}
                        >
                          {schedule?.name?.length > 30
                            ? schedule?.name?.substring(0, 27).trim() + "..."
                            : schedule?.name}
                        </TypographyNormal>{" "}
                      </Tooltip>
                    </Box>

                    {schedule.type === "SCHEDULE" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <TypographySmall
                          sx={{
                            fontWeight: 600,
                            color: purple[600],
                            textTransform: "capitalize",
                            mr: 1,
                          }}
                        >
                          {schedule.command.replace(/_/g, " ").toLowerCase()}
                        </TypographySmall>
                        <ClockIcon />
                        <TypographySmall sx={{ ml: 0.5, fontWeight: 600 }}>
                          {schedule.frequency === "OFF"
                            ? "ONCE"
                            : schedule.frequency}
                        </TypographySmall>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Link
                    to={
                      schedule.type === "ANNOUNCEMENT"
                        ? `/announcement/detail/${schedule.id}`
                        : `/schedule/detail/${schedule.id}`
                    }
                  >
                    <IconButton sx={{ color: colorStyling.black, ml: 4 }}>
                      <Tooltip title={tCommon("viewDetail.seeDetails")}>
                        <NavigateNextIcon fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </Link>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TypographyLarge sx={{ textAlign: "center", fontWeight: 300 }}>
            {tCommon("viewDetail.noScheduleData")}
          </TypographyLarge>
        )}
      </Box>
    </Box>
  );
};
